import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import StatisticsActions from '../../actions/statistics';
import { Card, CardContent, Typography, Grid, Box, Divider, Paper } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import SportsGolfIcon from '@material-ui/icons/SportsGolf';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import PlayCircleFilledWhiteIcon from '@material-ui/icons/PlayCircleFilledWhite';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ListAltIcon from '@material-ui/icons/ListAlt';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';

function Statistics({ stats, loading, error, fetchStats, downloadStatsCsv, downloadCsvLoading, downloadCsvError, downloadCsvBlob }: any) {
    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        if (downloadCsvBlob) {
            const url = window.URL.createObjectURL(new Blob([downloadCsvBlob], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            // Format date as DD-MM-YYYY for UK
            const now = new Date();
            const dd = String(now.getDate()).padStart(2, '0');
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const yyyy = now.getFullYear();
            const ukDate = `${dd}-${mm}-${yyyy}`;
            link.setAttribute('download', `Statistics Report - ${ukDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    }, [downloadCsvBlob]);

    const handleDownload = () => {
        downloadStatsCsv();
    };

    if (loading) return <div>Loading statistics...</div>;
    if (error) return <div>Error: {error.message || error}</div>;
    if (!stats) return <div>No statistics available.</div>;

    return (
        <Paper style={{ padding: 20, display: "block", paddingBottom: '2rem', height: "100%", maxHeight: "100vh", overflowY: "auto" }}>
            <Box p={3} mb={5}>
                <Typography variant="h4" gutterBottom>Statistics</Typography>
                <Box mb={2} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        onClick={handleDownload}
                        disabled={downloadCsvLoading}
                    >
                        {downloadCsvLoading ? 'Downloading...' : 'Download Stats'}
                    </Button>
                </Box>
                {downloadCsvError && <Typography color="error">Failed to download stats.</Typography>}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><GroupIcon color="primary" fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="h6">New Users</Typography>
                                        <Typography variant="h4">{stats.usersCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><SportsGolfIcon color="secondary" fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="h6">Games Finished</Typography>
                                        <Typography variant="h4">{stats.gamesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><ListAltIcon color="action" fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="h6">All Games</Typography>
                                        <Typography variant="h4">{stats.gamesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box my={4}><Divider /></Box>
                <Typography variant="h5" gutterBottom>Games by State</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><HourglassEmptyIcon style={{ color: '#fbc02d' }} fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1">Waiting List</Typography>
                                        <Typography variant="h5">{stats.waitingListGamesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><PlayCircleFilledWhiteIcon style={{ color: '#1976d2' }} fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1">Playing</Typography>
                                        <Typography variant="h5">{stats.playingGamesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card>
                            <CardContent>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item><CheckCircleIcon style={{ color: '#388e3c' }} fontSize="large" /></Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1">Done</Typography>
                                        <Typography variant="h5">{stats.doneGamesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
}

const mapStateToProps = (state: any) => ({
    stats: state.statistics.stats,
    loading: state.statistics.loading,
    error: state.statistics.error,
    downloadCsvLoading: state.statistics.downloadCsvLoading,
    downloadCsvError: state.statistics.downloadCsvError,
    downloadCsvBlob: state.statistics.downloadCsvBlob,
});

const mapDispatchToProps = {
    fetchStats: StatisticsActions.fetchStats,
    downloadStatsCsv: StatisticsActions.downloadStatsCsv,
};

export default connect(mapStateToProps, mapDispatchToProps)(Statistics);